// Telemetry for Phase 5.
//
// Three concerns:
//   - `reportError` — Sentry passthrough when initTelemetry has
//     installed an SDK; falls back to console.error otherwise. Called
//     from the ErrorBoundary, React Query mutation onError, and the
//     global window.onerror / unhandledrejection listeners.
//   - `reportVitals` — web-vitals beacon. Loaded lazily so the
//     instrumentation only pays its cost after first paint.
//   - `reportPageView` — fires on route changes. Hooked from a router
//     subscription in main.tsx.
//
// Sentry is real (`@sentry/browser` is installed) but only initializes
// when VITE_SENTRY_DSN is provided. Without a DSN the module imports
// pay only a one-time dynamic-import cost; init() is never called and
// the SDK's internal singleton stays cold.

type ErrorContext = {
  componentStack?: string | null;
  digest?: string;
};

let sentryCaptureException:
  | ((err: Error, ctx?: ErrorContext) => void)
  | null = null;

export function installGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (event) => {
    const err = event.error ?? new Error(event.message ?? 'unknown window error');
    reportError(err);
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const err =
      reason instanceof Error
        ? reason
        : new Error(`unhandled rejection: ${String(reason)}`);
    reportError(err);
  });
}

export async function initTelemetry(): Promise<void> {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;
  // Real Sentry SDK. Tree-shakes well enough that a no-DSN build pays
  // only the dynamic-import overhead (browsers cache the chunk; the
  // SDK's internal singleton never initializes).
  const sentry = await import('@sentry/browser');
  sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    // Defaults are reasonable for an internal tool; the team can dial
    // these once volume is observed.
    tracesSampleRate: 0.1,
  });
  sentryCaptureException = (err, ctx) =>
    sentry.captureException(err, { extra: ctx as unknown as Record<string, unknown> });
}

export function reportError(error: Error, info?: ErrorContext): void {
  if (sentryCaptureException) {
    sentryCaptureException(error, info);
    return;
  }
  console.error('[telemetry.reportError]', error, info?.componentStack);
}

export function reportPageView(pathname: string): void {
  if (import.meta.env.MODE === 'development') {
    console.debug('[telemetry.pageView]', pathname);
  }
  postBeacon('/v1/telemetry/page-views', {
    pathname,
    referrer: typeof document !== 'undefined' ? document.referrer : undefined,
    sessionId: getSessionId(),
    at: new Date().toISOString(),
  });
}

export type WebVitalsMetric = {
  name: 'CLS' | 'INP' | 'LCP' | 'FCP' | 'TTFB';
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
};

export function reportVitals(metric: WebVitalsMetric): void {
  if (import.meta.env.MODE === 'development') {
    console.debug(
      '[telemetry.vitals]',
      `${metric.name}=${metric.value.toFixed(1)} (${metric.rating})`,
    );
  }
  postBeacon('/v1/telemetry/vitals', {
    metric: metric.name,
    value: metric.value,
    rating: metric.rating,
    url: typeof location !== 'undefined' ? location.pathname : undefined,
    sessionId: getSessionId(),
  });
}

// Cheap session correlator. Persisted in sessionStorage so a single
// tab's pageviews/vitals can be threaded together server-side. Cleared
// on tab close — no PII, no tracking across sessions.
function getSessionId(): string | undefined {
  if (typeof sessionStorage === 'undefined') return undefined;
  let id = sessionStorage.getItem('pdm.sessionId');
  if (!id) {
    id = (typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    sessionStorage.setItem('pdm.sessionId', id);
  }
  return id;
}

// Fire-and-forget POST. Uses navigator.sendBeacon when available so
// the request survives a page unload; falls back to fetch with
// keepalive otherwise. The path is rooted at the api-gateway origin
// (the global apiBaseUrl set by ApiProvider isn't reachable here —
// telemetry init is pre-mount — so we read from VITE_PDM_API_BASE_URL
// if present, otherwise hit relative paths intercepted by MSW in dev
// and by the gateway in prod).
function postBeacon(path: string, body: unknown): void {
  if (typeof navigator === 'undefined') return;
  const base = (import.meta.env.VITE_PDM_API_BASE_URL as string | undefined) ?? '';
  const url = `${base}${path}`;
  const payload = JSON.stringify(body);
  try {
    if (typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([payload], { type: 'application/json' });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    }
    // Fall back to fetch keepalive — best-effort, fire and forget.
    void fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Telemetry must never throw into a caller's code path.
  }
}

export async function startVitalsCollector(): Promise<void> {
  const { onCLS, onINP, onLCP, onFCP, onTTFB } = await import('web-vitals');
  // web-vitals' Metric shape is a superset of our WebVitalsMetric, so
  // the cast picks the fields we care about and drops the rest.
  const adapt = (m: { name: string; value: number; rating: string }) =>
    reportVitals(m as WebVitalsMetric);
  onCLS(adapt);
  onINP(adapt);
  onLCP(adapt);
  onFCP(adapt);
  onTTFB(adapt);
}
